import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, forkJoin, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { DialogService } from 'shared/ui-common';
import { SearchOptions, objectToUrlParams, TreetopPropertiesProvider } from 'shared/util-common';

import {
  PaginationWrapperDto,
  CodingSystemValidationResult,
  ActivityDefinitionDto,
  ActivityRequestResourceType,
  CodingSystemExistenceCheckResult,
  ComponentCodeErrorCode,
  ComponentDto,
  ComponentLinkDto,
  UsableComponentFilter,
  ComponentLinkValueDto,
  ComponentReferencesResultDto,
  ComponentSearchResultDto,
  ComponentType,
  ConcurrencyChangesDto,
  ConcurrencyChangeType,
  ExtendedComponentFilter,
  FindingDefinitionDto,
  ProposedComponentDto,
  ValidationResult,
  AllComponentTypes,
} from '@treetop/shared/model';

import { LanguageService } from './language-service';
import { LoadingService } from './loading.service';
import { MessagesService } from './messages.service';
import {
  ConcurrencyChangesDialogComponent,
  ConcurrencyChangesDialogType,
} from '../concurrency-changes-dialog/concurrency-changes-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class MedicalComponentService {
  private http = inject(HttpClient);
  private loading = inject(LoadingService);
  private messages = inject(MessagesService);
  private dialog = inject(DialogService);
  private languageService = inject(LanguageService);

  baseUrl = `${TreetopPropertiesProvider.properties.baseUrl}api`;

  loadDefinitions(
    query: ExtendedComponentFilter = {},
    options: SearchOptions = { page: 1, itemsPerPage: 10, languageCode: 'de' },
    globalLoader = true,
  ): Observable<PaginationWrapperDto<AllComponentTypes>> {
    const optionParams = objectToUrlParams(options);
    const queryParams = objectToUrlParams(query);

    const loadDefinitions$ = this.http
      .get<PaginationWrapperDto<AllComponentTypes>>(`${this.baseUrl}/components?${queryParams}&${optionParams}`)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.messages.showError('CouldNotLoadDefinitions');
          return throwError(() => error);
        }),
        shareReplay(),
      );
    if (globalLoader) {
      this.loading.showLoaderUntilCompleted(loadDefinitions$).subscribe();
    }
    return loadDefinitions$;
  }

  loadDefinition(code: string, version = '1.0.0', hideErrorMsg?: boolean): Observable<AllComponentTypes> {
    return this.http.get<AllComponentTypes>(`${this.baseUrl}/components/${code}/${version}`).pipe(
      catchError((error: HttpErrorResponse) => {
        if (!hideErrorMsg) {
          this.messages.showError('CouldNotLoadDefinition');
        }
        return throwError(() => error);
      }),
      shareReplay(),
    );
  }

  loadProposedComponents(
    query: ExtendedComponentFilter = {},
    options: SearchOptions = { page: 1, itemsPerPage: 10, languageCode: 'de' },
  ): Observable<PaginationWrapperDto<ProposedComponentDto>> {
    const optionParams = objectToUrlParams(options);
    const queryParams = objectToUrlParams(query);

    const loadProposedComponents$ = this.http
      .get<PaginationWrapperDto<ProposedComponentDto>>(`${this.baseUrl}/proposed-components?${queryParams}&${optionParams}`)
      .pipe(
        catchError((err) => {
          this.messages.showError('CouldNotLoadProposedComponents');
          return throwError(err);
        }),
        shareReplay(),
      );
    this.loading.showLoaderUntilCompleted(loadProposedComponents$).subscribe();
    return loadProposedComponents$;
  }

  loadDefinitionsByType(
    query: ExtendedComponentFilter = {},
    definitionType: ComponentType = ComponentType.FindingDefinition,
  ): Observable<PaginationWrapperDto<FindingDefinitionDto>> {
    const paginationParams = objectToUrlParams({
      searchText: query.searchText,
      componentStates: query.componentStates,
      page: 1,
      itemsPerPage: 1000,
    });
    const queryParams = objectToUrlParams({ componentTypes: [definitionType] });
    return this.http.get<PaginationWrapperDto<FindingDefinitionDto>>(`${this.baseUrl}/components?${paginationParams}&${queryParams}`);
  }

  loadDefinitionsByTypeSubtype(
    query: ExtendedComponentFilter = {},
    definitionType: ComponentType = ComponentType.ActivityDefinition,
    linkDefintion: ActivityRequestResourceType = ActivityRequestResourceType.MedicationRequest,
  ): Observable<PaginationWrapperDto<ActivityDefinitionDto>> {
    const langCode = this.languageService.language.state;
    const paginationParams = objectToUrlParams({
      page: 1,
      itemsPerPage: 1000,
    });
    const queryParams = objectToUrlParams({
      searchText: query.searchText,
      componentTypes: definitionType,
    });
    const subTypeFilterString = `componentSubTypes[${definitionType}]=${linkDefintion}`;

    return this.http.get<PaginationWrapperDto<ActivityDefinitionDto>>(
      `${this.baseUrl}/components?${paginationParams}&languageCode=${langCode}&${queryParams}&${subTypeFilterString}`,
    );
  }
  loadUsableDefinitionsByType(query: UsableComponentFilter): Observable<ComponentSearchResultDto> {
    const queryParams = objectToUrlParams(query);
    let url = `${this.baseUrl}/components/usable?${queryParams}`;
    const langCode = this.languageService.language.state;
    url = `${url}&languageCode=${langCode}`;
    return this.http.get<ComponentSearchResultDto>(url);
  }

  addMedications(
    code: string,
    version: string,
    definition: ComponentDto,
    linkValues: ComponentLinkValueDto[],
  ): Observable<ComponentLinkDto> {
    const componentLinkDto: ComponentLinkDto = {
      toComponentCode: definition.code,
      toComponentVersion: definition.version,
      values: linkValues,
      linkType: 1,
    };
    return this.http.post<ComponentLinkDto>(`${this.baseUrl}/components/${code}/${version}/links`, componentLinkDto).pipe(
      catchError((error: HttpErrorResponse) => {
        this.messages.showError('MedicationError');
        return throwError(() => error);
      }),
      tap(() => this.messages.showInfo('MedicationAdded')),
      shareReplay(),
    );
  }

  addDefinition(changes: Partial<ComponentDto>): Observable<unknown> {
    return this.http.post(`${this.baseUrl}/components`, changes).pipe(
      catchError((error: HttpErrorResponse) => {
        this.messages.showError('DefinitionSaveError');
        return throwError(() => error);
      }),
      tap(() => this.messages.showInfo('DefinitionSaved')),
      shareReplay(),
    );
  }

  proposeDefinition(changes: Partial<ComponentDto>): Observable<ComponentDto> {
    return this.http.post(`${this.baseUrl}/components/propose`, changes).pipe(
      catchError((error: HttpErrorResponse) => {
        this.messages.showError('DefinitionSaveError');
        return throwError(() => error);
      }),
      tap(() => this.messages.showInfo('DefinitionSaved')),
      shareReplay(),
    );
  }

  updateProposedComponent(changes: Partial<ProposedComponentDto>): Observable<unknown> {
    return this.http.put(`${this.baseUrl}/proposed-components/${changes.code}`, changes).pipe(
      catchError((err: HttpErrorResponse) => {
        this.messages.showError('ProposedComponentsSaveError');
        return throwError(() => err);
      }),
      tap(() => this.messages.showInfo('ProposedComponentSaved')),
      shareReplay(),
    );
  }

  updateDefinition(changes: Partial<ComponentDto>): Observable<unknown> {
    return this.isEditAllowed(changes, true).pipe(
      switchMap((isAllowed) => {
        if (isAllowed) {
          return this.http.put(`${this.baseUrl}/components/${changes.code}/${changes.version}`, changes).pipe(
            catchError((error: HttpErrorResponse) => {
              this.messages.showError('DefinitionSaveError');
              return throwError(() => error);
            }),
            tap(() => this.messages.showInfo('DefinitionSaved')),
            shareReplay(),
          );
        } else {
          return of({ notAllowed: true });
        }
      }),
    );
  }

  private isEditAllowed(updatedDefinition: Partial<ComponentDto>, enableForceOverwrite = false): Observable<boolean> {
    return this.http
      .post<ConcurrencyChangesDto>(
        `${this.baseUrl}/components/${updatedDefinition.code}/${updatedDefinition.version}/concurrency-changes`,
        updatedDefinition,
      )
      .pipe(
        switchMap((concurrencyChanges) => {
          if (concurrencyChanges.hasChanged) {
            if (concurrencyChanges.type === ConcurrencyChangeType.Content && enableForceOverwrite) {
              const dialogRef = this.dialog.open(ConcurrencyChangesDialogComponent, {
                title: 'ConcurrencyChangesDialogTitle',
                data: {
                  type: ConcurrencyChangesDialogType.MedicalComponent,
                  concurrencyChanges: concurrencyChanges,
                },
              });
              return dialogRef.afterClosed().pipe(
                map((forceOverwrite: boolean) => {
                  return !!forceOverwrite;
                }),
              );
            } else {
              this.dialog.open(ConcurrencyChangesDialogComponent, {
                title: 'ConcurrencyChangesDialogTitle',
                data: {
                  type: ConcurrencyChangesDialogType.MedicalComponent,
                  concurrencyChanges: concurrencyChanges,
                },
              });
              return of(false);
            }
          } else {
            return of(true);
          }
        }),
        catchError((error: HttpErrorResponse) => {
          this.messages.showError('ConcurrencyChangeCheckError');
          return throwError(() => error);
        }),
        shareReplay(),
      );
  }

  validateTreetopCode(code: string, type: ComponentType | string): Observable<[boolean, ValidationResult<ComponentCodeErrorCode>]> {
    const encodedCode = encodeURIComponent(code);

    let typeUrl = type;
    if (type == 'Guideline') {
      typeUrl = 'guidelines';
    }

    let validationUrl = `${this.baseUrl}/${typeUrl}/${encodedCode}/validate`;
    let existsUrl = `${this.baseUrl}/${typeUrl}/${encodedCode}/exists`;

    if (ComponentType[type]) {
      validationUrl = `${this.baseUrl}/components/${encodedCode}/validate?type=${type}`;
      existsUrl = `${this.baseUrl}/components/${encodedCode}/exists`;
    }

    const exists = this.http.get<boolean>(existsUrl);
    const valid = this.http.get<ValidationResult<ComponentCodeErrorCode>>(validationUrl);
    return forkJoin([exists, valid]).pipe(
      catchError((error: HttpErrorResponse) => {
        this.messages.showError('ErrorValidatingTreetopcode');
        return throwError(() => error);
      }),
      shareReplay(),
    );
  }

  codingSystemExistenceCheck(
    codingSystemName: string,
    code: string,
    componentCode: string = null,
  ): Observable<CodingSystemExistenceCheckResult> {
    let url = `${this.baseUrl}/components/coding-system/${codingSystemName}/${code}/exists`;
    if (componentCode !== null) {
      url += `?componentCode=${componentCode}`;
    }
    return this.http.get<CodingSystemExistenceCheckResult>(url).pipe(
      catchError((error) => {
        this.messages.showError('ErrorValidatingCodingSystemCode');
        return throwError(() => error);
      }),
    );
  }

  validateCodingSystem(codingSystemName: string, code: string): Observable<CodingSystemValidationResult> {
    const url = `${this.baseUrl}/components/coding-system/${codingSystemName}/${code}/validate`;
    return this.http.get<CodingSystemValidationResult>(url).pipe(
      catchError((error) => {
        this.messages.showError('ErrorValidatingCodingSystemCode');
        return throwError(() => error);
      }),
    );
  }

  public setDefinitionState(definitions: ComponentDto[], definitionState: string): Observable<unknown> {
    const concurrencyChangeRequests: Observable<ConcurrencyChangesDto>[] = [];
    for (const definition of definitions) {
      concurrencyChangeRequests.push(
        this.http.post<ConcurrencyChangesDto>(
          `${this.baseUrl}/components/${definition.code}/${definition.version}/concurrency-changes`,
          definition,
        ),
      );
    }

    return forkJoin(concurrencyChangeRequests).pipe(
      switchMap((concurrencyChangeResults) => {
        const haveDefinitionsConcurrencyChanges = concurrencyChangeResults.some((result) => result.hasChanged);
        if (!haveDefinitionsConcurrencyChanges) {
          const codes = definitions.map((definition) => definition.code);
          return this.http.put<void>(`${this.baseUrl}/components/${definitionState}`, codes);
        } else {
          const immutableConcurrencyChangeResult = concurrencyChangeResults.find(
            (result) => result.hasChanged && result.type !== ConcurrencyChangeType.Content,
          );
          this.dialog.open(ConcurrencyChangesDialogComponent, {
            title: 'ConcurrencyChangesDialogTitle',
            data: {
              type: ConcurrencyChangesDialogType.MedicalComponent,
              concurrencyChanges: immutableConcurrencyChangeResult
                ? immutableConcurrencyChangeResult
                : { hasChanged: true, type: ConcurrencyChangeType.None },
            },
          });
          return of({ notAllowed: true });
        }
      }),
    );
  }

  getReferencesForDefinition(code: string): Observable<ComponentReferencesResultDto> {
    const getReferencesForDefinition$ = this.http.get<ComponentReferencesResultDto>(`${this.baseUrl}/components/${code}/references`).pipe(
      map((result) => {
        return result;
      }),
      catchError((error: HttpErrorResponse) => {
        this.messages.showError('CouldNotLoadReferencesOfComponent');
        return throwError(() => error);
      }),
      shareReplay(),
    );

    this.loading.showLoaderUntilCompleted(getReferencesForDefinition$).subscribe();
    return getReferencesForDefinition$;
  }

  deleteDefinition(definition: ComponentDto): Observable<unknown> {
    return this.isEditAllowed(definition).pipe(
      switchMap((isAllowed) => {
        if (isAllowed) {
          return this.http.delete(`${this.baseUrl}/components/${definition.code}`).pipe(
            catchError((error: HttpErrorResponse) => {
              this.messages.showError('CouldNotDeleteComponent');
              return throwError(() => error);
            }),
            tap(() => this.messages.showInfo('ComponentSuccessfullyDeleted')),
            shareReplay(),
          );
        } else {
          return of({ notAllowed: true });
        }
      }),
    );
  }

  addLink(code: string, version: string, definition: ComponentDto, linkValues: ComponentLinkValueDto[]): Observable<ComponentLinkDto> {
    const componentLinkDto: ComponentLinkDto = {
      toComponentCode: definition.code,
      toComponentVersion: definition.version,
      values: linkValues,
    };
    return this.http.post<ComponentLinkDto>(`${this.baseUrl}/components/${code}/${version}/link`, componentLinkDto).pipe(
      catchError((error: HttpErrorResponse) => {
        this.messages.showError('AddLinkError');
        return throwError(() => error);
      }),
      tap(() => this.messages.showInfo('LinkAdded')),
      shareReplay(),
    );
  }

  loadLinks(componentCode: FormControl, version: FormControl): Observable<ComponentLinkDto[]> {
    const langCode = this.languageService.language.state;
    return this.http
      .get<ComponentLinkDto[]>(`${this.baseUrl}/components/${componentCode?.value}/${version?.value}/links?languageCode=${langCode}`)
      .pipe(
        map((result) => {
          return result;
        }),
        catchError((error: HttpErrorResponse) => {
          this.messages.showError('CouldNotLoadLinks');
          return throwError(() => error);
        }),
        shareReplay(),
      );
  }

  public loadMedicationRequests(componentCode: string, version: string): Observable<ActivityDefinitionDto[]> {
    const langCode = this.languageService.language.state;
    return this.http
      .get<ActivityDefinitionDto[]>(`${this.baseUrl}/components/${componentCode}/${version}/medication-requests?languageCode=${langCode}`)
      .pipe(
        map((result) => {
          return result;
        }),
        catchError((error: HttpErrorResponse) => {
          this.messages.showError('CouldNotLoadMedicationRequests');
          return throwError(() => error);
        }),
        shareReplay(),
      );
  }

  deleteLink(link: ComponentLinkValueDto): Observable<unknown> {
    return this.http
      .delete(
        `${this.baseUrl}/components/links?fromComponentCode=${link?.fromComponentCode}&fromComponentVersion=${link?.fromComponentVersion}&toComponentCode=${link?.toComponentCode}&toComponentVersion=${link?.toComponentVersion}`,
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.messages.showError('LinkDeleteError');
          return throwError(() => error);
        }),
        tap(() => this.messages.showInfo('LinkDeleted')),
        shareReplay(),
      );
  }

  loadMedicationGroup(componentCode: FormControl, version: FormControl): Observable<ComponentLinkDto[]> {
    const langCode = this.languageService.language.state;
    return this.http
      .get<
        ComponentLinkDto[]
      >(`${this.baseUrl}/components/${componentCode?.value}/${version?.value}/links?languageCode=${langCode}&linkType=1`)
      .pipe(
        map((result) => {
          return result;
        }),
        catchError((error: HttpErrorResponse) => {
          this.messages.showError('CouldNotLoadMedicalGroup');
          return throwError(() => error);
        }),
        shareReplay(),
      );
  }

  deleteMedication(link: ComponentLinkDto): Observable<unknown> {
    return this.http
      .delete(
        `${this.baseUrl}/components/${link.fromComponentCode}/${link.fromComponentVersion}/links?toComponentCode=${link.toComponentCode}&toComponentVersion=${link.toComponentVersion}&linkType=1`,
      )
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.messages.showError('MedicationDeleteError');
          return throwError(() => error);
        }),
        tap(() => this.messages.showInfo('MedicationDeleted')),
        shareReplay(),
      );
  }

  loadLatestMedicalComponent(code: string, hideErrorMsg = false): Observable<AllComponentTypes> {
    return this.http.get<AllComponentTypes>(`${this.baseUrl}/components/${code}/latest`).pipe(
      catchError((error: HttpErrorResponse) => {
        if (!hideErrorMsg) {
          this.messages.showError('CouldNotLoadDefinition');
        }
        return throwError(() => error);
      }),
      shareReplay(),
    );
  }
}
